import {DI_TOKENS} from '@/di/di.tokens';
import {ClickerState} from '@/stores/ClickerStateStore/ClickerStateStore.types';
import {GameStatusType} from '@/stores/GameStatusStore/GameStatusStore.constants';
import {ResultGiftType} from '@/stores/GiftsStore/GiftsStore.constants';

import {injected} from 'brandi';
import GUI, {Controller} from 'lil-gui';

import {ProjectUserAuth} from '@specials/project-user';

import {DevPanel} from '@/services/DevPanel/DevPanel';
import {LocationType, ProjectUserInfoData, ProjectUserLoginData} from '@/services/ProjectAuthApi/types';
import {ClickerStateStore} from '@/stores/ClickerStateStore/ClickerStateStore';
import {GameStatusStore} from '@/stores/GameStatusStore/GameStatusStore';
import {UserStore} from '@/stores/UserStore/UserStore';
import {WinnersStore} from '@/stores/WinnersStore/WinnersStore';
import {CallbackCollector} from '@/utils/CallbackCollector';

import {CookieStorage} from '../CookieStorage/CookieStorage';
import {IProjectApi} from '../ProjectApi/types';

export class DevPanelService {
  public config: {
    Локация: LocationType;
    Энергия: number;
    ['Игровой статус']: GameStatusType;
    Logout: VoidFunction;
    Скрыть: VoidFunction;
    ['Сбросить онбординг']: VoidFunction;
    UseSSOMock: boolean;
  };
  public winnerConfig: {
    addWinnerStocks: VoidFunction;
    addWinnerMoney: VoidFunction;
    addWinnerTShop: VoidFunction;
    clearWinnerDT: VoidFunction;
  };
  private energyController: Controller;
  private guiInstance: GUI;

  constructor(
    private readonly gui: DevPanel,
    private readonly userStore: UserStore,
    private readonly clickerStateStore: ClickerStateStore,
    private readonly gameStatusStore: GameStatusStore,
    private readonly winnersStore: WinnersStore,
    private readonly callbackCollector: CallbackCollector,
    private readonly projectUserAuth: ProjectUserAuth<ProjectUserInfoData, ProjectUserLoginData>,
    private readonly cookieStorage: CookieStorage,
    private readonly projectApi: IProjectApi,
  ) {}

  public init(): void {
    this.guiInstance = this.gui.getInstance();
    this.guiInstance.domElement.style.right = '0';
    this.guiInstance.domElement.style.zIndex = '2010';

    this.config = {
      Локация: this.userStore.getLocationType(),
      Энергия: this.clickerStateStore.getEnergyChargeCount(),
      ['Игровой статус']: this.gameStatusStore.getStatus(),
      Logout: () => void this.projectUserAuth.logout(),
      Скрыть: () => this.guiInstance.hide(),
      ['Сбросить онбординг']: this.resetOnboarding,
      UseSSOMock: this.cookieStorage.getUseSSOMocks() === 'true',
    };

    this.guiInstance
      .add(this.config, 'Локация', {
        '1': LocationType.Location1,
        '2': LocationType.Location2,
        '3': LocationType.Location3,
        '4': LocationType.Location4,
      })
      .onChange(this.handleChangeLocation);

    this.energyController = this.guiInstance.add(this.config, 'Энергия').onChange(this.handleEnergyChange);
    this.guiInstance
      .add(this.config, 'Игровой статус', {
        active: GameStatusType.Active,
        'leagues-recalculation': GameStatusType.LeaguesRecalculation,
        'game-finished-before-results': GameStatusType.GameFinishedBeforeResults,
        'game-finished-after-results': GameStatusType.GameFinishedAfterResults,
      })
      .onChange(this.handleGameStatusTypeChange);
    this.guiInstance.add(this.config, 'Logout');
    this.guiInstance.add(this.config, 'Сбросить онбординг');
    this.guiInstance.add(this.config, 'Скрыть');
    this.guiInstance.add(this.config, 'UseSSOMock').onChange(this.handleChangeUseSSOMock);

    this.winnerConfig = {
      addWinnerStocks: () => {
        this.setWinnerDT(ResultGiftType.Stocks);
      },
      addWinnerMoney: () => {
        this.setWinnerDT(ResultGiftType.Money);
      },
      addWinnerTShop: () => {
        this.setWinnerDT(ResultGiftType.TShop);
      },
      clearWinnerDT: () => {
        this.winnersStore.setWinnerDT(null);
        this.winnersStore.updateWinner();
      },
    };

    const winnerFolder = this.gui.getInstance().addFolder('Winner');

    winnerFolder.add(this.winnerConfig, 'addWinnerStocks');
    winnerFolder.add(this.winnerConfig, 'addWinnerMoney');
    winnerFolder.add(this.winnerConfig, 'addWinnerTShop');
    winnerFolder.add(this.winnerConfig, 'clearWinnerDT');

    this.callbackCollector.add(this.clickerStateStore.subscribe(this.handleClickerStateUpdated));
  }

  public destroy(): void {
    this.guiInstance?.destroy();
    this.callbackCollector.execute();
  }

  private resetOnboarding = async (): Promise<void> => {
    await this.projectApi.setOnboardingStatus('0 0');
    location.reload();
  };

  private handleClickerStateUpdated = (state: Omit<ClickerState, 'improvements' | 'actualAt'>): void => {
    this.config.Энергия = Math.round(state.energy);
    if (this.energyController) {
      this.energyController.updateDisplay();
    }
  };

  private handleChangeLocation = (v: LocationType): void => this.userStore.setLocationType(v);

  private handleEnergyChange = (v: number): void => this.clickerStateStore.devSetEnergy(v);

  private handleGameStatusTypeChange = (v: GameStatusType): void => this.gameStatusStore.setStatus(v);

  private handleChangeUseSSOMock = (v: boolean): void => this.cookieStorage.setUseSSOMocks(String(v));

  private setWinnerDT = (type: ResultGiftType): void => {
    const userId = this.userStore.getUserId();
    if (userId) {
      this.winnersStore.setWinnerDT({
        type,
        id: userId,
        name: 'Тестовый П.',
        phone: '+7 ••• ••• 12 34',
        ticket: '000-000-00',
      });
      this.winnersStore.updateWinner();
    }
  };
}

injected(
  DevPanelService,
  DI_TOKENS.devPanel,
  DI_TOKENS.userStore,
  DI_TOKENS.clickerStateStore,
  DI_TOKENS.gameStatusStore,
  DI_TOKENS.winnersStore,
  DI_TOKENS.callbackCollector,
  DI_TOKENS.projectUserAuth,
  DI_TOKENS.cookieStorage,
  DI_TOKENS.projectApi,
);
